import { ACCOUNT_APP_ID, ACCOUNT_CALLBACK_ORIGIN } from '@/config/domains';
import type { AuthType } from '@/types';
import { authApi } from './authApi';
import {
  buildOAuthAuthorizeURL,
  consumeOAuthTransaction,
  createCodeChallenge,
  createCodeVerifier,
  createRandomState,
  decodeOAuthState,
  encodeOAuthState,
  getOAuthCallbackStorageKey,
  isOAuthCallbackStoragePayload,
  OAUTH_CALLBACK_STORAGE_KEY_PREFIX,
  readOAuthCallbackParams,
  saveOAuthTransaction,
} from './oauthWebSession';

const OAUTH_REDIRECT_URI = `${ACCOUNT_CALLBACK_ORIGIN}/login/callback`;
const OAUTH_POPUP_NAME = 'account-oauth-login';
const OAUTH_POPUP_TIMEOUT_MS = 5 * 60 * 1000;
const OAUTH_RESULT_TTL_MS = 5 * 60 * 1000;
const pendingOAuthFlows = new Map<
  string,
  {
    flowID: string;
    provider: 'google' | 'github';
    appID: string;
    redirectURI: string;
    codeVerifier?: string;
    createdAt: number;
    returnPath: string;
    nonce: string;
  }
>();

function openOAuthPopup(authorizeURL: string): Window {
  const popup = window.open(
    authorizeURL,
    OAUTH_POPUP_NAME,
    'popup=yes,width=520,height=720,left=200,top=120,resizable=yes,scrollbars=yes',
  );
  if (!popup) {
    throw new Error('无法打开登录窗口，请检查浏览器弹窗拦截设置');
  }
  popup.focus();
  return popup;
}

function consumeOAuthCallbackStorageResult(flowID: string): string | null {
  const key = getOAuthCallbackStorageKey(flowID);
  const raw = window.localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  window.localStorage.removeItem(key);

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isOAuthCallbackStoragePayload(parsed) || parsed.flowID !== flowID) {
      return null;
    }
    return parsed.callbackURL;
  } catch {
    return null;
  }
}

function cleanupExpiredOAuthResults(now: number): void {
  const keysToDelete: string[] = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (!key || !key.startsWith(OAUTH_CALLBACK_STORAGE_KEY_PREFIX)) {
      continue;
    }

    const raw = window.localStorage.getItem(key);
    if (!raw) {
      keysToDelete.push(key);
      continue;
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        !isOAuthCallbackStoragePayload(parsed) ||
        now - parsed.receivedAt > OAUTH_RESULT_TTL_MS
      ) {
        keysToDelete.push(key);
      }
    } catch {
      keysToDelete.push(key);
    }
  }

  for (const key of keysToDelete) {
    window.localStorage.removeItem(key);
  }
}

function cleanupExpiredPendingOAuthFlows(now: number): void {
  for (const [flowID, flow] of pendingOAuthFlows.entries()) {
    if (now - flow.createdAt > OAUTH_RESULT_TTL_MS) {
      pendingOAuthFlows.delete(flowID);
    }
  }
}

function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function normalizeReturnPath(returnPath: string): string {
  const normalized = returnPath.trim();
  if (
    !normalized.startsWith('/') ||
    normalized.startsWith('//') ||
    normalized.startsWith('/login/callback')
  ) {
    return '/brand-selection';
  }

  return normalized;
}

function waitForOAuthStorageResult(
  flowID: string,
  popup: Window,
): Promise<string> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const storageKey = getOAuthCallbackStorageKey(flowID);

    const resolveFromStorage = (): boolean => {
      const callbackURL = consumeOAuthCallbackStorageResult(flowID);
      if (!callbackURL) {
        return false;
      }

      cleanup();
      try {
        popup.close();
      } catch {
        // ignore popup close failure after successful callback
      }
      resolve(callbackURL);
      return true;
    };

    const closeInterval = window.setInterval(() => {
      if (resolveFromStorage()) {
        return;
      }
      if (popup.closed) {
        cleanup();
        reject(new Error('登录窗口已关闭，请重新发起登录'));
      }
    }, 500);

    const timeoutTimer = window.setTimeout(() => {
      if (resolveFromStorage()) {
        return;
      }
      cleanup();
      try {
        popup.close();
      } catch {
        // ignore popup close failure during timeout cleanup
      }
      reject(new Error('登录超时，请重新发起登录'));
    }, OAUTH_POPUP_TIMEOUT_MS);

    const handleStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== window.localStorage ||
        event.key !== storageKey
      ) {
        return;
      }
      resolveFromStorage();
    };

    const cleanup = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearInterval(closeInterval);
      window.clearTimeout(timeoutTimer);
      window.removeEventListener('storage', handleStorage);
    };

    window.addEventListener('storage', handleStorage);
    resolveFromStorage();
  });
}

function authTypeToProvider(type: AuthType): 'google' | 'github' {
  switch (type) {
    case 1:
      return 'google';
    case 2:
      return 'github';
    default:
      throw new Error(`Unsupported auth type: ${type}`);
  }
}

export const accountService = {
  async loginWithSocial(type: AuthType): Promise<string> {
    const provider = authTypeToProvider(type);
    const response = await authApi.getOAuthProviderConfig(
      provider,
      ACCOUNT_APP_ID,
    );
    if (response.code !== 0 || !response.data) {
      throw new Error(response.errmsg || '获取登录配置失败');
    }

    const createdAt = Date.now();
    cleanupExpiredPendingOAuthFlows(createdAt);
    cleanupExpiredOAuthResults(createdAt);

    const flowID = createRandomState();
    const nonce = createRandomState();
    const returnPath = currentReturnPath();
    const state = encodeOAuthState({
      flowID,
      provider,
      returnPath,
      nonce,
    });
    const codeVerifier =
      provider === 'google' ? createCodeVerifier() : undefined;
    const codeChallenge = codeVerifier
      ? await createCodeChallenge(codeVerifier)
      : undefined;

    pendingOAuthFlows.set(flowID, {
      flowID,
      provider,
      appID: ACCOUNT_APP_ID,
      redirectURI: OAUTH_REDIRECT_URI,
      codeVerifier,
      createdAt,
      returnPath,
      nonce,
    });

    saveOAuthTransaction({
      flowID,
      provider,
      appID: ACCOUNT_APP_ID,
      nonce,
      state,
      redirectURI: OAUTH_REDIRECT_URI,
      returnPath,
      codeVerifier,
      createdAt,
    });

    const authorizeURL = buildOAuthAuthorizeURL({
      authorizeURL:
        response.data.authorize_url || response.data.authorizeUrl || '',
      clientID: response.data.client_id || response.data.clientId || '',
      redirectURI: OAUTH_REDIRECT_URI,
      scope: response.data.scope,
      state,
      extraAuthorizeParams:
        response.data.extra_authorize_params ||
        response.data.extraAuthorizeParams,
      codeChallenge,
    });

    const popup = openOAuthPopup(authorizeURL);
    try {
      return await waitForOAuthStorageResult(flowID, popup);
    } catch (error) {
      pendingOAuthFlows.delete(flowID);
      throw error;
    }
  },

  async completeOAuthCallback(callbackURL: string): Promise<string> {
    const params = readOAuthCallbackParams(callbackURL);
    if (params.error) {
      throw new Error(params.errorDescription || params.error);
    }
    if (!params.code || !params.state) {
      throw new Error('登录回调缺少必要参数');
    }

    const decodedState = decodeOAuthState(params.state);
    const pendingFlow = pendingOAuthFlows.get(decodedState.flowID);
    const transaction = pendingFlow ?? consumeOAuthTransaction();
    if (!transaction) {
      throw new Error('登录请求已失效，请重新发起登录');
    }
    if (pendingFlow) {
      pendingOAuthFlows.delete(decodedState.flowID);
      consumeOAuthTransaction();
    }

    if (
      transaction.provider !== decodedState.provider ||
      transaction.nonce !== decodedState.nonce ||
      transaction.redirectURI !== OAUTH_REDIRECT_URI ||
      transaction.flowID !== decodedState.flowID
    ) {
      throw new Error('登录状态校验失败，请重新登录');
    }

    const response = await authApi.loginForOAuthWebSession({
      provider: transaction.provider,
      code: params.code,
      state: params.state,
      redirect_uri: transaction.redirectURI,
      code_verifier: transaction.codeVerifier,
      app_id: transaction.appID,
    });
    if (response.code !== 0) {
      throw new Error(response.errmsg || '登录失败');
    }

    return normalizeReturnPath(
      transaction.returnPath || decodedState.returnPath,
    );
  },
};
